import { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import {
  UploadOnAddEvent,
  UploadOnProgressEvent,
  UploadOnRemoveEvent,
  UploadOnStatusChangeEvent,
} from '@progress/kendo-react-upload';
import { produce } from 'immer';
import moment, { Moment } from 'moment';
import React, { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { showErrorToast } from '../../../../../../../common/global/helpers';
import useBreakpoints from '../../../../../../../common/hooks/useBreakpoints';
import DatePicker from '../../../../../../../common/widgets/DatePicker';
import Dropdown from '../../../../../../../common/widgets/Dropdown';
import FileInput from '../../../../../../../common/widgets/FileInput';
import Icons from '../../../../../../../common/widgets/Icons';
import PopoverDialog from '../../../../../../../common/widgets/PopoverDialog';
import { ErrorState, ValidationError } from '../../../../../models/Errors';
import {
  PresenceTypes,
  Timesheet,
} from '../../../../../models/TimeAndAttendance';
import { resources } from '../../../../../resources/resources';
import { timeAndAttendanceService } from '../../../../../services/time-and-attendance';
import { TimeAndAttendanceStep } from '../enum';

interface IVacationFormProps {
  selectedEvent: Timesheet | undefined;
  setSelectedEvent: React.Dispatch<React.SetStateAction<Timesheet | undefined>>;
  setActiveStep: React.Dispatch<React.SetStateAction<TimeAndAttendanceStep>>;
  setTimesheet: React.Dispatch<React.SetStateAction<Timesheet[] | undefined>>;
  setShowAttendanceDialog: React.Dispatch<React.SetStateAction<boolean>>;
  currentDate: Moment;

  availableTimesheetPresenceTypes: PresenceTypes[];
  errors: ErrorState;
  setErrors: Dispatch<SetStateAction<ErrorState>>;
}

export const VacationForm = (props: IVacationFormProps) => {
  const { isMobile } = useBreakpoints();
  const { t: translate } = useTranslation();

  let currentDate = new Date();

  const handleEventDetails = () => {
    if (validateForm()) return;

    props.setActiveStep(TimeAndAttendanceStep.Confirm);
  };

  const handleEventTypeChange = (e: DropDownListChangeEvent) => {
    props.setSelectedEvent(
      produce(draft => {
        if (draft) {
          draft.presenceType = e.value;
        }
      }),
    );
  };

  const handleStartDateChange = (value: Date | null) => {
    props.setSelectedEvent(
      produce(draft => {
        if (draft && value) {
          draft.startDate = value;
        }
      }),
    );
  };

  const handleEndDateChange = (value: Date | null) => {
    props.setSelectedEvent(
      produce(draft => {
        if (draft && value) {
          draft.endDate = value;
        }
      }),
    );
  };

  const handleRemoveEvent = async () => {
    if (props.selectedEvent?.id) {
      const abortController = new AbortController();

      const result =
        await timeAndAttendanceService.removeTimesheetPresenceEvent(
          props.selectedEvent?.id,
          {
            signal: abortController?.signal,
          },
        );

      if (abortController?.signal.aborted) {
        return;
      }

      if (!result.hasErrors) {
        if (result.data) {
          props.setTimesheet(prevState => {
            if (prevState) {
              return [
                ...prevState.filter(t => t.id !== props.selectedEvent?.id),
              ];
            } else {
              return [];
            }
          });
        }
      } else {
        showErrorToast(translate(resources.errors.generic));
      }
    }

    props.setShowAttendanceDialog(false);
  };

  const onAdd = (event: UploadOnAddEvent) => {
    props.setSelectedEvent(
      produce(draft => {
        if (draft) {
          draft.files = event.newState;
        }
      }),
    );
  };

  const onRemove = (event: UploadOnRemoveEvent) => {
    props.setSelectedEvent(
      produce(draft => {
        if (draft) {
          draft.files = event.newState;
        }
      }),
    );
  };

  const onProgress = (event: UploadOnProgressEvent) => {
    props.setSelectedEvent(
      produce(draft => {
        if (draft) {
          draft.files = event.newState;
        }
      }),
    );
  };

  const onStatusChange = (event: UploadOnStatusChangeEvent) => {
    props.setSelectedEvent(
      produce(draft => {
        if (draft) {
          draft.files = event.newState;
        }
      }),
    );
  };

  const validateForm = (): boolean => {
    props.setErrors(
      produce(d => {
        if (!props.selectedEvent?.presenceType) {
          d.presenceTypeId = [getRequiredValidationError('presenceTypeId')];
        } else {
          delete d.presenceTypeId;
        }

        if (
          !props.selectedEvent?.startDate ||
          !moment(props.selectedEvent?.startDate, 'YYYY-MM-DD').isValid()
        ) {
          d.workDataStartDate = [
            getRequiredValidationError('workDataStartDate'),
          ];
        } else {
          delete d.workDataStartDate;
        }

        if (
          !props.selectedEvent?.endDate ||
          !moment(props.selectedEvent?.endDate, 'YYYY-MM-DD').isValid()
        ) {
          d.workDataEndDate = [getRequiredValidationError('workDataEndDate')];
        } else {
          delete d.workDataEndDate;
        }
      }),
    );

    return (
      !props.selectedEvent?.presenceType ||
      !props.selectedEvent?.startDate ||
      !moment(props.selectedEvent?.startDate, 'YYYY-MM-DD').isValid() ||
      !props.selectedEvent?.endDate ||
      !moment(props.selectedEvent?.endDate, 'YYYY-MM-DD').isValid()
    );
  };

  const getRequiredValidationError = (target: string): ValidationError => ({
    target,
    code: resources.validation.required,
    severity: 'error',
    placeholders: {},
  });

  return (
    <div
      className={`vacation-form-container ${
        props.selectedEvent?.id && 'form-edit'
      }`}
    >
      <Dropdown
        mandatory
        label={
          props.selectedEvent?.id
            ? translate(resources.timeAndAttendance.editVacationTypeLabel)
            : translate(resources.timeAndAttendance.vacationTypeLabel)
        }
        data={props.availableTimesheetPresenceTypes}
        textField="presenceTypeDescription"
        dataItemKey="presenceTypeId"
        value={props.selectedEvent?.presenceType}
        onChange={handleEventTypeChange}
        errors={props.errors.presenceTypeId}
      />

      <label>
        {translate(resources.timeAndAttendance.vacationInterval)}
        <sup>*</sup>
      </label>
      <div className="vacation-interval-container">
        <DatePicker
          value={props.selectedEvent?.startDate ?? currentDate}
          onChange={handleStartDateChange}
          minDate={props.currentDate.startOf('month').toDate()}
          maxDate={
            !props.selectedEvent?.id ? props.selectedEvent?.endDate : undefined
          }
          errors={props.errors.workDataStartDate}
        />
        {!props.selectedEvent?.id && (
          <>
            <span className="btn-icon">
              <Icons.ArrowRight />
            </span>
            <DatePicker
              value={props.selectedEvent?.endDate ?? currentDate}
              errors={props.errors.workDataEndDate}
              onChange={handleEndDateChange}
              minDate={props.selectedEvent?.startDate}
              maxDate={props.currentDate.endOf('month').toDate()}
            />
          </>
        )}
      </div>

      {!props.selectedEvent?.id && (
        <FileInput
          autoUpload={false}
          showActionButtons={false}
          batch={false}
          files={props.selectedEvent?.files}
          multiple={true}
          restrictions={{
            allowedExtensions: ['.pdf'],
            maxFileSize: 900000,
          }}
          label={translate(resources.timeAndAttendance.fileUploadLabel)}
          id="vacation-file-input"
          onBeforeRemove={e => {
            e.additionalData.fileId = (e.files[0] as any).fileId;
          }}
          errors={props.errors.files}
          onAdd={onAdd}
          onRemove={onRemove}
          onProgress={onProgress}
          onStatusChange={onStatusChange}
          withCredentials={false}
          helper={translate(resources.timeAndAttendance.fileUploadHelperText)}
        />
      )}

      <div className="vacation-form-actions">
        {props.selectedEvent?.id && (
          <div>
            <PopoverDialog
              className="info-tooltip"
              placement="bottom"
              type="hover"
              title={translate(resources.timeAndAttendance.delete)}
            >
              <button className="btn btn-delete" onClick={handleRemoveEvent}>
                <span className="btn-icon">
                  <Icons.Delete />
                </span>
              </button>
            </PopoverDialog>
          </div>
        )}
        <button
          className={`btn btn-solid ${isMobile && 'btn-mobile'}`}
          onClick={handleEventDetails}
        >
          {props.selectedEvent?.id
            ? translate(resources.timeAndAttendance.save)
            : translate(resources.timeAndAttendance.add)}
        </button>
      </div>
    </div>
  );
};
